import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Activity, Loader2, Database as DbIcon, CheckCircle2, AlertTriangle, Clock, Radio } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { CustomDropdown } from '../../../components/CustomDropdown ';
import baseapi from '../../../services/baseapi';

interface ConnectionSession {
  database: string;
  state: string;
  query: string;
  user: string;
  service: string;
  duration_sec: number;
  avg_time_ms?: number;
  total_time_ms?: number;
}

interface ConnectionsTabProps {
  activeCluster: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${
              isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
            }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-200 text-[9px] truncate max-w-[130px]" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const ConnectionsTab: React.FC<ConnectionsTabProps> = ({
  activeCluster,
  clientId,
  refreshKey,
  dbType,
}) => {
  const [loading, setLoading] = useState(true);
  const [metricsData, setMetricsData] = useState<any>(null);
  const [connTrendData, setConnTrendData] = useState<any>(null);
  const [perDbConnData, setPerDbConnData] = useState<any>(null);
  const [sessions, setSessions] = useState<ConnectionSession[]>([]);

  const isMongo = ['mongodb', 'mongo'].includes((dbType || '').toLowerCase());
  const isES = ['opensearch', 'elasticsearch'].includes((dbType || '').toLowerCase());
  const isMongoOrES = isMongo || isES;

  // Hover & Filter States
  const [hoveredTrend, setHoveredTrend] = useState<string | null>(null);
  const [hoveredPerDb, setHoveredPerDb] = useState<string | null>(null);
  const [dbFilter, setDbFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const params: any = { cluster_id: activeCluster };
    if (clientId) params.client_id = clientId;

    const safeGet = async (url: string, p: any) => {
      try {
        const res = await baseapi.get(url, { params: p });
        return res.data?.data || res.data;
      } catch {
        return null;
      }
    };

    try {
      const [mRes, trendRes, perDbRes, sessRes] = await Promise.all([
        safeGet('/db_monitor_dashboard/metrics', params),
        safeGet('/db_monitor_dashboard/graph/connections', params),
        isMongoOrES ? null : safeGet('/db_monitor_dashboard/graph/per_db/connections', params),
        isES ? null : safeGet('/db_monitor_dashboard/active_queries', { ...params, size: 200 }),
      ]);

      setMetricsData(mRes);
      setConnTrendData(trendRes);
      setPerDbConnData(perDbRes);
      setSessions(sessRes || []);
    } catch (err) {
      console.warn('ConnectionsTab fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId, isMongoOrES]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  // Trend graph series
  const activeKey = isMongo ? 'Active Ops' : isES ? 'Open Connections' : 'Active';
  const totalKey = isMongo ? 'Connected Clients' : 'Total';

  const connTrendFormatted = useMemo(() => {
    if (!connTrendData || !connTrendData.labels) return [];
    const timeLabels = formatTimeLabels(connTrendData.labels);
    return connTrendData.labels.map((_: any, idx: number) => ({
      time: timeLabels[idx],
      [activeKey]: connTrendData.series?.connections_active?.[idx] || 0,
      [totalKey]: connTrendData.series?.connections_total?.[idx] || 0,
      Waiting: connTrendData.series?.connections_waiting?.[idx] || 0,
    }));
  }, [connTrendData, activeKey, totalKey]);

  // Per DB connections series
  const perDbFormatted = useMemo(() => {
    if (!perDbConnData || !perDbConnData.labels || !perDbConnData.databases) return [];
    const timeLabels = formatTimeLabels(perDbConnData.labels);
    return perDbConnData.labels.map((_: any, idx: number) => {
      const point: any = { time: timeLabels[idx] };
      perDbConnData.databases.forEach((db: string) => {
        point[db] = perDbConnData.series?.[`${db}:connections`]?.[idx] || 0;
      });
      return point;
    });
  }, [perDbConnData]);
  const dbOptions = useMemo(() => {
    return Array.from(new Set(sessions.map((s) => s.database).filter(Boolean))).sort();
  }, [sessions]);

  const dbDropdownData = useMemo(() => {
    return [
      { label: 'All Databases', value: 'all' },
      ...dbOptions.map((db) => ({ label: db, value: db })),
    ];
  }, [dbOptions]);

  const filteredSessions = useMemo(() => {
    return sessions.filter((s) => {
      if (dbFilter !== 'all' && s.database !== dbFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          (s.database || '').toLowerCase().includes(q) ||
          (s.query || '').toLowerCase().includes(q) ||
          (s.user || s.service || '').toLowerCase().includes(q) ||
          (s.state || '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [sessions, dbFilter, searchQuery]);

  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#06b6d4', '#ef4444'];
  const hasData = (arr: any[]) => arr && arr.length > 0;

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.1;
  };

  const getDot = (_hovered?: any, _key?: any, _color?: any, _defaultR?: any) => false;

  const activeCount = metricsData?.connections_active ?? sessions.filter((s) => s.state === 'active').length;
  const idleCount = metricsData?.connections_idle ?? 0;
  const waitingCount = metricsData?.connections_waiting ?? sessions.filter((s) => s.state?.includes('idle in transaction')).length;
  const totalCount = metricsData?.connections_total ?? sessions.length;
  const maxConns = metricsData?.connections_max ?? 100;
  const utilPct = metricsData?.connections_util_pct ?? (maxConns > 0 ? (totalCount / maxConns) * 100 : 0);

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }
  return (
    <div className="space-y-4 relative">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded bg-orange-500  text-white">
            <Radio className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white tracking-wide">
              {isMongo
                ? 'MongoDB Connection Activity & Pool Monitor'
                : 'Active Connections & Pool Monitor'}
            </h2>
          </div>
        </div>
      </div>

      {/* 5 Glassmorphic Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        {/* Active / Ops Executing */}
        <div className="bg-gradient-to-br from-green-950/30 to-gray-900/80 border border-green-500/30 rounded-xl p-3 shadow-lg">
          <div className="flex items-center justify-between text-xs text-gray-400 font-semibold mb-1">
            <span>{isMongo ? 'Ops Executing' : isES ? 'Open Connections' : 'Active'}</span>
            <Activity className="w-3.5 h-3.5 text-green-400" />
          </div>
          <div className="text-xl font-bold font-mono text-green-400">{activeCount}</div>
        </div>

        {/* Idle */}
        <div className="bg-gradient-to-br from-gray-900/80 to-gray-900/90 border border-gray-800 rounded-xl p-3 shadow-lg">
          <div className="flex items-center justify-between text-xs text-gray-400 font-semibold mb-1">
            <span>Idle</span>
            <Clock className="w-3.5 h-3.5 text-gray-400" />
          </div>
          <div className="text-xl font-bold font-mono text-gray-300">{idleCount}</div>
        </div>

        {/* Idle in Txn */}
        <div className="bg-gradient-to-br from-amber-950/30 to-gray-900/80 border border-amber-500/30 rounded-xl p-3 shadow-lg">
          <div className="flex items-center justify-between text-xs text-gray-400 font-semibold mb-1">
            <span>Idle in Txn</span>
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
          </div>
          <div className="text-xl font-bold font-mono text-amber-400">{waitingCount}</div>
        </div>

        {/* Total */}
        <div className="bg-gradient-to-br from-blue-950/30 to-gray-900/80 border border-blue-500/30 rounded-xl p-3 shadow-lg">
          <div className="flex items-center justify-between text-xs text-gray-400 font-semibold mb-1">
            <span>{isMongo ? 'Connected Clients' : 'Total / Max'}</span>
            <DbIcon className="w-3.5 h-3.5 text-blue-400" />
          </div>
          <div className="text-xl font-bold font-mono text-white">
            {totalCount}{' '}
            {!isMongo && <span className="text-xs text-gray-400 font-normal">/ {maxConns}</span>}
          </div>
        </div>

        {/* Utilization */}
        <div className="bg-gradient-to-br from-orange-950/30 to-gray-900/80 border border-orange-500/30 rounded-xl p-3 shadow-lg">
          <div className="flex items-center justify-between text-xs text-gray-400 font-semibold mb-1">
            <span>Pool Utilization</span>
            <CheckCircle2 className="w-3.5 h-3.5 text-orange-400" />
          </div>
          <div className="text-xl font-bold font-mono text-orange-400">
            {Number(utilPct).toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Trend Graphs */}
      <div className={`grid grid-cols-1 ${isMongoOrES ? '' : 'md:grid-cols-2'} gap-4`}>
        {/* Connection Activity (over time) */}
        <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
          <div className="mb-3 border-b border-gray-700/50 pb-2 flex justify-between items-center">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">
              {isMongo
                ? 'Connection Activity (over time)'
                : isES
                ? 'Open HTTP Connections (over time)'
                : 'Total Active Sessions (Trend)'}
            </h4>
            <span className="text-[10px] font-mono text-gray-400">conns</span>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(connTrendFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={connTrendFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredTrend} onHoverSeries={setHoveredTrend} />} />
                  <Line
                    type="monotone"
                    dataKey={activeKey}
                    stroke="#10b981"
                    strokeOpacity={getOpacity(hoveredTrend, activeKey)}
                    strokeWidth={2}
                    dot={getDot()}
                  />
                  <Line
                    type="monotone"
                    dataKey={totalKey}
                    stroke="#3b82f6"
                    strokeOpacity={getOpacity(hoveredTrend, totalKey)}
                    strokeWidth={1.5}
                    dot={getDot()}
                  />
                  {!isMongo && (
                    <Line
                      type="monotone"
                      dataKey="Waiting"
                      stroke="#f59e0b"
                      strokeOpacity={getOpacity(hoveredTrend, 'Waiting')}
                      strokeWidth={1.5}
                      dot={getDot()}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No connection trend data available</span>
            )}
          </div>
        </div>

        {/* Active Connections per Database (PG / MSSQL only) */}
        {!isMongoOrES && (
          <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
            <div className="mb-3 border-b border-gray-700/50 pb-2 flex justify-between items-center">
              <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Connections per Database</h4>
              <span className="text-[10px] font-mono text-gray-400">conns</span>
            </div>
            <div className="h-44 w-full flex items-center justify-center">
              {hasData(perDbFormatted) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={perDbFormatted}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                    <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredPerDb} onHoverSeries={setHoveredPerDb} />} />
                    {(perDbConnData?.databases || []).map((db: string, i: number) => (
                      <Line
                        key={db}
                        type="monotone"
                        dataKey={db}
                        stroke={colors[i % colors.length]}
                        strokeOpacity={getOpacity(hoveredPerDb, db)}
                        strokeWidth={hoveredPerDb === db ? 2.5 : 1.5}
                        dot={getDot()}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No per-database connections telemetry available</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Active Sessions & Live Operations Table (PG, MSSQL, MongoDB only) */}
      {!isES && (
        <div className="bg-gray-900/80 border border-gray-700/60 rounded-xl shadow-xl overflow-hidden mt-4">
          {/* Table Header Controls */}
          <div className="p-3 border-b border-gray-700/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-900/90">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-white" />
              <h3 className="text-xs font-bold text-white uppercase tracking-wider font-mono">
                {isMongo ? 'Live Operations / Active Connections' : 'Live Sessions & Active Queries'} ({filteredSessions.length})
              </h3>
            </div>

            <div className="flex items-center gap-2">
              {/* Database Filter Custom Dropdown */}
              {dbOptions.length > 0 && (
                <CustomDropdown
                  data={dbDropdownData}
                  selectedValue={dbFilter}
                  onSelect={(val) => setDbFilter(val)}
                  width="w-44"
                  buttonHeight={8}
                  placeholder="All Databases"
                />
              )}

              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search query, database, user..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 font-mono w-48 sm:w-64"
                />
              </div>
            </div>
          </div>

          {/* Table Body */}
          <div className="overflow-x-auto custom-scroll max-h-96">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-gray-700/80 text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-900/90 sticky top-0 z-10">
                  <th className="py-2.5 px-3">DATABASE</th>
                  <th className="py-2.5 px-3">STATE</th>
                  <th className="py-2.5 px-3">{isMongo ? 'OPERATION / COMMAND' : 'QUERY'}</th>
                  <th className="py-2.5 px-3 text-right">DURATION</th>
                  <th className="py-2.5 px-3 text-right">AVG TIME</th>
                  <th className="py-2.5 px-3 text-right">TOTAL TIME</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/60 font-mono text-xs">
                {filteredSessions.length > 0 ? (
                  filteredSessions.map((q, idx) => {
                    const state = q.state || 'active';
                    const isAct = state === 'active';
                    const isIdleTxn = state.includes('idle in transaction');
                    const dur = q.duration_sec || 0;
                    const durStr = dur >= 1 ? `${dur.toFixed(1)}s` : `${(dur * 1000).toFixed(0)}ms`;
                    const durColor = dur > 10 ? 'text-red-400 font-bold' : dur > 3 ? 'text-amber-400 font-bold' : 'text-gray-200';

                    const avg = q.avg_time_ms || 0;
                    const avgStr = avg >= 1000 ? `${(avg / 1000).toFixed(2)}s` : `${avg.toFixed(1)}ms`;

                    const total = q.total_time_ms || 0;
                    const totalStr = total >= 1000 ? `${(total / 1000).toFixed(2)}s` : `${total.toFixed(0)}ms`;

                    return (
                      <tr key={idx} className="hover:bg-orange-500/[0.06] transition-colors">
                        <td className="py-2 px-3 text-orange-400 font-bold">{q.database || '—'}</td>
                        <td className="py-2 px-3">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                              isAct
                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                : isIdleTxn
                                ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                                : 'bg-gray-800 text-gray-400 border border-gray-700'
                            }`}
                          >
                            {state}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-gray-300 max-w-xs truncate" title={q.query || ''}>
                          {q.query || '—'}
                        </td>
                        <td className={`py-2 px-3 text-right ${durColor}`}>{durStr}</td>
                        <td className="py-2 px-3 text-right text-gray-300">{avgStr}</td>
                        <td className="py-2 px-3 text-right text-orange-400 font-bold">{totalStr}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-xs text-gray-500 italic">
                      No active sessions or live operations found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
